<script setup lang="ts">
import { computed, nextTick, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { ComponentExposed } from 'vue-component-type-helpers'
import { ApolloError } from '@apollo/client/core'
import {
  ClearImageStepUploadEditMutation,
  ClearImageStepUploadEditMutationVariables,
  ImageEditorDataQuery,
  ImageEditorDataQueryVariables,
  ImageEditorDraftDataQuery,
  ImageEditorDraftDataQueryVariables,
  ImageOptions,
  SaveImageStepUploadEditMutation,
  SaveImageStepUploadEditMutationVariables,
} from '@/generated/graphql'
import { DraftImageEdit, isEmptyImageEdit, parseImageEdit } from '@/utils/imageEdit'
import useUploads from '@/composables/useUploads'
import ImageEditorCanvas, {
  AnnotationTool,
  EditorMode,
} from '@/components/stepUpload/ImageEditorCanvas.vue'
import NotificationFailed from '@/components/notifications/NotificationFailed.vue'

const props = defineProps<{
  id?: string
  draftUploadId?: string
  addDraft?: (edit: DraftImageEdit | undefined) => Promise<void>
}>()
const emit = defineEmits<{
  saved: []
}>()

const { t } = useI18n()
const { uploadFile } = useUploads()

const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/heic', 'image/heif']
const COLORS = ['#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#000000', '#ffffff']
const THICKNESSES = [1, 2, 4, 6]
const sourceOptions: ImageOptions = { width: 2560, height: 2560 }

const isOpen = ref(false)
const isFullscreen = ref(false)
const isDirty = ref(false)
const saving = ref(false)
const loadFailed = ref(false)
const discardConfirmOpen = ref(false)
const showFailedNotification = ref(false)
const mode = ref<EditorMode>('annotate')
const image = shallowRef<HTMLImageElement>()
const canvas = ref<ComponentExposed<typeof ImageEditorCanvas>>()

const isDraft = computed(() => !props.id)

const fetchQuery = useQuery<ImageEditorDataQuery, ImageEditorDataQueryVariables>(
  gql`
    query ImageEditorData($id: ID!, $options: ImageOptions!) {
      product {
        imageStepUpload(id: $id) {
          id
          editLayer
          originalUpload {
            id
            name
            contentType
            extension {
              ... on ImageExtension {
                url(options: $options)
              }
            }
          }
          upload {
            id
            name
            contentType
            extension {
              ... on ImageExtension {
                url(options: $options)
              }
            }
          }
        }
      }
    }
  `,
  () => ({ id: props.id ?? '', options: sourceOptions }),
  () => ({ enabled: isOpen.value && !!props.id }),
)

const draftQuery = useQuery<ImageEditorDraftDataQuery, ImageEditorDraftDataQueryVariables>(
  gql`
    query ImageEditorDraftData($id: ID!, $options: ImageOptions!) {
      upload {
        upload(id: $id) {
          id
          name
          contentType
          extension {
            ... on ImageExtension {
              url(options: $options)
            }
          }
        }
      }
    }
  `,
  () => ({ id: props.draftUploadId ?? '', options: sourceOptions }),
  () => ({ enabled: isOpen.value && isDraft.value && !!props.draftUploadId }),
)

const stepUpload = computed(() => fetchQuery.result.value?.product.imageStepUpload)
const source = computed(() =>
  isDraft.value
    ? draftQuery.result.value?.upload.upload
    : (stepUpload.value?.originalUpload ?? stepUpload.value?.upload),
)
const initialEdit = computed(() =>
  isDraft.value ? undefined : parseImageEdit(stepUpload.value?.editLayer),
)

const tools: { value: AnnotationTool; icon: string }[] = [
  { value: 'select', icon: 'near_me' },
  { value: 'text', icon: 'title' },
  { value: 'rectangle', icon: 'crop_square' },
  { value: 'ellipse', icon: 'radio_button_unchecked' },
  { value: 'line', icon: 'horizontal_rule' },
  { value: 'arrow', icon: 'north_east' },
]

const stageHeight = computed(() => (isFullscreen.value ? 'calc(100vh - 170px)' : '65vh'))

const saveMutation = useMutation<
  SaveImageStepUploadEditMutation,
  SaveImageStepUploadEditMutationVariables
>(gql`
  mutation SaveImageStepUploadEdit($command: SaveImageStepUploadEdit!) {
    product {
      saveImageStepUploadEdit(command: $command) {
        id
      }
    }
  }
`)
const clearMutation = useMutation<
  ClearImageStepUploadEditMutation,
  ClearImageStepUploadEditMutationVariables
>(gql`
  mutation ClearImageStepUploadEdit($id: ID!) {
    product {
      clearImageStepUploadEdit(id: $id) {
        id
      }
    }
  }
`)

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image()
    element.crossOrigin = 'anonymous'
    element.onload = () => resolve(element)
    element.onerror = () => reject(new Error('Could not load the image'))
    element.src = url
  })

const open = async () => {
  image.value = undefined
  loadFailed.value = false
  isDirty.value = false
  mode.value = 'annotate'
  isFullscreen.value = false
  isOpen.value = true
  try {
    await nextTick()
    await (isDraft.value ? draftQuery : fetchQuery).refetch()
    const extension = source.value?.extension
    if (!extension || !('url' in extension)) throw new Error('The upload is not an image')
    image.value = await loadImage(extension.url)
  } catch {
    loadFailed.value = true
  }
}

const close = () => {
  discardConfirmOpen.value = false
  isOpen.value = false
  image.value = undefined
}

const requestClose = () => {
  if (isDirty.value) {
    discardConfirmOpen.value = true
  } else {
    close()
  }
}

const uploadRender = async (editor: NonNullable<typeof canvas.value>) => {
  const isPhoto = PHOTO_CONTENT_TYPES.includes(source.value?.contentType ?? '')
  const mimeType = isPhoto ? 'image/jpeg' : 'image/png'
  const blob = await editor.render(mimeType, isPhoto ? 0.9 : undefined)
  const baseName = source.value?.name?.replace(/\.[^.]+$/, '') || 'image'
  const file = new File([blob], `${baseName} (edited).${isPhoto ? 'jpg' : 'png'}`, {
    type: mimeType,
  })
  return uploadFile(file)
}

const save = async () => {
  const editor = canvas.value
  if (!editor) return
  const edit = editor.getEdit()
  const hasEdits = !isEmptyImageEdit(edit)

  saving.value = true
  try {
    if (isDraft.value) {
      const uploadId = hasEdits ? await uploadRender(editor) : undefined
      await props.addDraft?.(uploadId ? { uploadId, editLayer: JSON.stringify(edit) } : undefined)
    } else if (isDirty.value && hasEdits) {
      const uploadId = await uploadRender(editor)
      await saveMutation.mutate({
        command: { id: props.id as string, uploadId, editLayer: JSON.stringify(edit) },
      })
    } else if (isDirty.value && stepUpload.value?.originalUpload) {
      await clearMutation.mutate({ id: props.id as string })
    }
    close()
    if (!isDraft.value) emit('saved')
  } catch (error) {
    if (!(error instanceof ApolloError)) showFailedNotification.value = true
  } finally {
    saving.value = false
  }
}

defineExpose({ open })
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="isFullscreen"
    :max-width="isFullscreen ? undefined : 1240"
    persistent
  >
    <v-card>
      <div class="editor-toolbar">
        <div class="editor-tools">
          <v-btn-toggle v-model="mode" mandatory density="compact" color="primary">
            <v-btn value="annotate" prepend-icon="edit" size="small">
              {{ t('entity.stepUpload.imageEditor.mode.annotate') }}
            </v-btn>
            <v-btn value="crop" prepend-icon="crop" size="small">
              {{ t('entity.stepUpload.imageEditor.mode.crop') }}
            </v-btn>
          </v-btn-toggle>

          <template v-if="canvas && mode === 'annotate'">
            <v-divider vertical class="toolbar-divider" />
            <v-btn
              v-for="tool in tools"
              :key="tool.value"
              :icon="tool.icon"
              size="small"
              :variant="canvas.tool === tool.value ? 'tonal' : 'text'"
              :color="canvas.tool === tool.value ? 'primary' : undefined"
              :title="t(`entity.stepUpload.imageEditor.tool.${tool.value}`)"
              @click="canvas.setTool(tool.value)"
            />
            <v-divider vertical class="toolbar-divider" />
            <v-menu>
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  size="small"
                  variant="text"
                  icon
                  :title="t('entity.stepUpload.imageEditor.button.color')"
                >
                  <span class="color-swatch" :style="{ background: canvas.color }" />
                </v-btn>
              </template>
              <v-card class="pa-2 d-flex ga-1">
                <v-btn
                  v-for="swatch in COLORS"
                  :key="swatch"
                  size="small"
                  variant="text"
                  icon
                  @click="canvas.setColor(swatch)"
                >
                  <span
                    class="color-swatch"
                    :class="{ active: canvas.color === swatch }"
                    :style="{ background: swatch }"
                  />
                </v-btn>
              </v-card>
            </v-menu>
            <v-menu>
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon="line_weight"
                  size="small"
                  variant="text"
                  :title="t('entity.stepUpload.imageEditor.button.thickness')"
                />
              </template>
              <v-list density="compact">
                <v-list-item
                  v-for="value in THICKNESSES"
                  :key="value"
                  :active="canvas.thickness === value"
                  @click="canvas.setThickness(value)"
                >
                  <div class="thickness-sample" :style="{ height: `${value}px` }" />
                </v-list-item>
              </v-list>
            </v-menu>
            <v-divider vertical class="toolbar-divider" />
            <v-btn
              icon="undo"
              size="small"
              variant="text"
              :disabled="!canvas.canUndo"
              :title="t('entity.stepUpload.imageEditor.button.undo')"
              @click="canvas.undo()"
            />
            <v-btn
              icon="redo"
              size="small"
              variant="text"
              :disabled="!canvas.canRedo"
              :title="t('entity.stepUpload.imageEditor.button.redo')"
              @click="canvas.redo()"
            />
            <v-btn
              icon="delete"
              size="small"
              variant="text"
              :disabled="!canvas.hasSelection"
              :title="t('entity.stepUpload.imageEditor.button.delete')"
              @click="canvas.deleteSelected()"
            />
          </template>

          <template v-if="canvas && mode === 'crop'">
            <v-divider vertical class="toolbar-divider" />
            <v-btn size="small" variant="text" prepend-icon="crop_free" @click="canvas.resetCrop()">
              {{ t('entity.stepUpload.imageEditor.button.resetCrop') }}
            </v-btn>
          </template>
        </div>

        <div class="editor-window-controls">
          <v-btn
            icon="restart_alt"
            size="small"
            variant="text"
            :disabled="!canvas"
            :title="t('entity.stepUpload.imageEditor.button.removeEdits')"
            @click="canvas?.removeAllEdits()"
          />
          <v-btn
            :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
            size="small"
            variant="text"
            :title="
              isFullscreen
                ? t('entity.stepUpload.imageEditor.button.minimize')
                : t('entity.stepUpload.imageEditor.button.maximize')
            "
            @click="isFullscreen = !isFullscreen"
          />
          <v-btn
            icon="close"
            size="small"
            variant="text"
            :title="t('button.close')"
            @click="requestClose"
          />
        </div>
      </div>

      <div class="editor-hint text-caption text-medium-emphasis px-4 py-2">
        <v-icon size="small" class="mr-1">info</v-icon>
        {{ t(`entity.stepUpload.imageEditor.hint.${mode}`) }}
      </div>

      <div class="editor-stage bg-grey-lighten-4" :style="{ height: stageHeight }">
        <image-editor-canvas
          v-if="image"
          ref="canvas"
          :image="image"
          :initial-edit="initialEdit"
          :mode="mode"
          @dirty="isDirty = true"
        />
        <v-alert
          v-else-if="loadFailed"
          type="error"
          variant="tonal"
          class="ma-6"
          :text="t('entity.stepUpload.imageEditor.imageLoadFailed')"
        />
        <v-progress-circular v-else indeterminate color="primary" />
      </div>

      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="saving" @click="requestClose">
          {{ t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="saving"
          :disabled="!canvas"
          @click="save"
        >
          {{ t('button.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="discardConfirmOpen" max-width="450">
    <v-card>
      <v-card-text class="pt-4">
        {{ t('entity.stepUpload.imageEditor.discardConfirmText') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="discardConfirmOpen = false">
          {{ t('button.cancel') }}
        </v-btn>
        <v-btn color="primary" variant="elevated" @click="close">
          {{ t('button.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <notification-failed v-model="showFailedNotification" />
</template>

<style scoped lang="scss">
.editor-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 4px 8px;
}

.editor-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding-right: 130px;
}

.toolbar-divider {
  height: 24px;
  align-self: center;
  margin: 0 6px;
}

.editor-window-controls {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
}

.editor-hint {
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.editor-stage {
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);

  &.active {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }
}

.thickness-sample {
  width: 64px;
  border-radius: 4px;
  background: currentColor;
}
</style>
